/* eslint-disable @typescript-eslint/no-shadow */
import './ServiceMarkAsComplete.scss';

import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { Button, message, Progress, Typography } from 'antd';
import TMIcon from 'tm-icons-library';

import { UploadOutlined } from '@ant-design/icons';

import Header from '../../../../components/Header/Header';
import DotsFlashingLoader from '../../../../components/V2/Loader/DotsFlashingLoader';
import { useTheme } from '../../../../context/ThemeProvider';
import { uploadFilesInServiceRequest } from '../../../../context/thunks/tqConnectServiceRequestThunk';
import { resetShareFeedbackModalState } from '../../../../context/tqConnectServiceRequestReducer';
import { useUpdateTQServiceRequestResponse } from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import { DELETED_USER } from '../../../../shared/data';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { createColorShades } from '../../../../shared/utils';
import { THEME, ThemeType } from '../../../../types/baseTypes';
import {
  EUploadModuleType,
  IFileAttachment,
} from '../../../../types/fileTypes';
import {
  ITQRatingCategories,
  ITQRatingCategory,
  TQRatingCategoryValue,
} from '../../../../types/tqConnectTypes';
import {
  ITQServiceRequestResponseDetailsUser,
  ITQUpdateServiceRequestResponseReview,
} from '../../../../types/tqConnectTypes/service-requests';
import { TQ_VALIDATION } from '../../constants';
import ServiceMarkAsCompleteDescriptionView from './ServiceMarkAsCompleteDescriptionView';

type ViewMode = 'form' | 'success';

interface Props {
  serviceRequestId?: string;
  serviceRequestResponseId?: string;
  respondedBy?: ITQServiceRequestResponseDetailsUser | null;
  onClose?: () => void;
  onSuccess?: () => void;
}

const initialFormValues: ITQUpdateServiceRequestResponseReview = {
  rating: null,
  feedback: '',
  attachments: [],
};

const ServiceMarkAsComplete: React.FC<Props> = ({
  serviceRequestId: propServiceRequestId,
  serviceRequestResponseId: propServiceRequestResponseId,
  respondedBy: propRespondedBy,
  onClose,
  onSuccess,
}) => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { colors: COLORS } = useTheme();

  const { shareFeedbackModalState } = useAppSelector(
    (state) => state.tqConnectServiceRequest,
  );

  const serviceRequestId =
    propServiceRequestId || shareFeedbackModalState.serviceRequestId;
  const serviceRequestResponseId =
    propServiceRequestResponseId ||
    shareFeedbackModalState.serviceRequestResponseId;
  const respondedBy = propRespondedBy ?? shareFeedbackModalState.respondedBy;

  const [viewMode, setViewMode] = useState<ViewMode>('form');
  const [formValues, setFormValues] =
    useState<ITQUpdateServiceRequestResponseReview>(initialFormValues);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const previewUrlsRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachmentPreviews = useMemo(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    const list = attachments.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    previewUrlsRef.current = list.map(({ url }) => url);
    return list;
  }, [attachments]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    },
    [],
  );

  const handleAddAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    const maxCount =
      TQ_VALIDATION.MAX_SERVICE_REQUEST_RESPONSE_FEEDBACK_ATTACHMENTS_COUNT;
    const maxSize = TQ_VALIDATION.MAX_SERVICE_REQUEST_FILE_SIZE;
    const valid: File[] = [];
    for (const file of files) {
      if (valid.length >= maxCount) {
        message.warning(`Maximum ${maxCount} attachment(s) allowed`);
        break;
      }
      if (file.size > maxSize) {
        message.warning(
          `File "${file.name}" exceeds ${maxSize / 1024 / 1024} MB`,
        );
        continue;
      }
      valid.push(file);
    }
    if (valid.length > 0) {
      setAttachments((prev) => [...prev, ...valid].slice(0, maxCount));
    }
  };

  const handleRemoveAttachment = (file: File) => {
    setAttachments((prev) => prev.filter((f) => f !== file));
  };

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const { ratings, selectedRatingCategory } = useMemo(() => {
    const tqConnectConfig = tqConnectConfigResult?.tqConnectConfig;
    const ratingCategories = (tqConnectConfig?.ratingCategories ||
      {}) as ITQRatingCategories;
    const ratingsEntries: [TQRatingCategoryValue, ITQRatingCategory][] =
      Object.entries(ratingCategories) as [
        TQRatingCategoryValue,
        ITQRatingCategory,
      ][];

    return {
      ratings: ratingsEntries,
      selectedRatingCategory: formValues.rating
        ? ratingCategories?.[formValues.rating]
        : null,
    };
  }, [formValues.rating, tqConnectConfigResult?.tqConnectConfig]);

  const { updateServiceRequestResponse, isPending: isSubmittingReview } =
    useUpdateTQServiceRequestResponse(
      serviceRequestId || '',
      serviceRequestResponseId || '',
    );

  const isSaving = useMemo(
    () => isSubmittingReview || isUploading,
    [isSubmittingReview, isUploading],
  );

  const isSubmitDisabled = useMemo(
    () => isSaving || !formValues.rating || !formValues.feedback.trim(),
    [isSaving, formValues.rating, formValues.feedback],
  );

  const openDescriptionView = () => {
    setIsDescriptionOpen(true);
  };

  const closeDescriptionView = () => {
    setIsDescriptionOpen(false);
  };

  const saveDescriptionOnly = (description: string) => {
    setFormValues((prev) => ({ ...prev, feedback: description }));
    setIsDescriptionOpen(false);
  };

  const handleClose = () => {
    dispatch(resetShareFeedbackModalState());
    setViewMode('form');
    setFormValues(initialFormValues);
    setAttachments([]);
    setUploadProgress(0);
    onClose?.();
    navigate.goBack();
  };

  const onSelectRating = (value: TQRatingCategoryValue) => {
    setFormValues((prev) => ({ ...prev, rating: value }));
  };

  const submitFeedback = async () => {
    if (!formValues.rating || !serviceRequestId || !serviceRequestResponseId) {
      message.warning('Please rate your overall experience');
      return;
    }
    if (!formValues.feedback.trim()) {
      message.warning('Please write your feedback');
      return;
    }

    try {
      let uploadedFiles: IFileAttachment[] = [];

      if (attachments.length > 0) {
        const resp = await dispatch(
          uploadFilesInServiceRequest(
            attachments,
            setIsUploading,
            (event) => {
              if (
                typeof (event as { progress?: number }).progress === 'number'
              ) {
                setUploadProgress((event as { progress: number }).progress);
              }
            },
            EUploadModuleType.TQ_CONNECT_SERVICE_REQUEST_RESPONSE,
          ),
        );

        if (resp && resp?.length > 0) {
          uploadedFiles = resp;
        }
      }

      const reqBody: ITQUpdateServiceRequestResponseReview = {
        rating: formValues.rating,
        feedback: formValues.feedback,
        attachments: [...(formValues?.attachments || []), ...uploadedFiles],
      };

      updateServiceRequestResponse(
        {
          serviceRequestId,
          serviceRequestResponseId,
          data: { status: 'reviewed', review: reqBody },
        },
        {
          onSuccess: () => {
            message.success('Your feedback has been submitted');
            setViewMode('success');
            onSuccess?.();
          },
          onError: () => {
            setUploadProgress(0);
            setIsUploading(false);
          },
        },
      );
    } catch (error) {
      showAppError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const displayName = respondedBy?.name || DELETED_USER;

  if (!serviceRequestId || !serviceRequestResponseId) {
    return null;
  }

  if (viewMode === 'success') {
    return (
      <div className="service-mark-as-complete page__container">
        <div className="page__content service-mark-as-complete__success">
          <div className="service-mark-as-complete__success__icon-wrap">
            <TMIcon
              name="checkmark-circle"
              size={80}
              color={COLORS.SUCCESS500}
              className="service-mark-as-complete__success__icon"
            />
          </div>
          <Typography.Title
            level={3}
            className="service-mark-as-complete__success__title">
            Completed Successfully!
          </Typography.Title>
          <Typography.Paragraph className="service-mark-as-complete__success__message">
            Your feedback has been submitted and the request with{' '}
            <Typography.Text strong>{displayName}</Typography.Text> is now
            marked as completed.
          </Typography.Paragraph>
          <Button
            type="primary"
            className="siteBtn accentBtnV1 service-mark-as-complete__success__done"
            onClick={handleClose}
            block>
            Done
          </Button>
        </div>
      </div>
    );
  }

  if (isDescriptionOpen) {
    const descriptionPlaceholder =
      selectedRatingCategory?.placeholder ??
      'What did they do that really made a difference?';
    return (
      <ServiceMarkAsCompleteDescriptionView
        initialDescription={formValues.feedback}
        placeholder={descriptionPlaceholder}
        onDone={saveDescriptionOnly}
        onClose={closeDescriptionView}
      />
    );
  }

  return (
    <div className="service-mark-as-complete page__container">
      <Header
        title=""
        handleBack={handleClose}
        backType="cross"
        customTitle={
          <Typography.Title
            level={5}
            className="service-mark-as-complete__header-title">
            Mark as Completed & Share Feedback
          </Typography.Title>
        }
      />
      <div className="page__content__scroll service-mark-as-complete__scroll">
        {uploadProgress > 0 ? (
          <div className="service-mark-as-complete__uploading">
            <div className="service-mark-as-complete__uploading__inner">
              <div className="service-mark-as-complete__uploading__title">
                <Typography.Title level={4}>Finishing up</Typography.Title>
                <DotsFlashingLoader color={COLORS.WHITE} size={6} />
              </div>
              <Progress
                percent={Math.round(uploadProgress * 100)}
                status="active"
                strokeColor={COLORS.WHITE}
                showInfo={false}
                size="small"
                trailColor={COLORS.ICON}
              />
              <Typography.Paragraph className="service-mark-as-complete__uploading__subtitle">
                Your feedback is being processed. Please wait...
              </Typography.Paragraph>
            </div>
          </div>
        ) : (
          <>
            <Typography.Paragraph className="service-mark-as-complete__intro">
              Please rate and share feedback to complete this request with{' '}
              <Typography.Text strong style={{ color: COLORS.TEXT1 }}>
                {displayName}
              </Typography.Text>
              .
            </Typography.Paragraph>

            <Typography.Text className="service-mark-as-complete__question">
              How would you rate your overall experience with {displayName}?
            </Typography.Text>

            <div
              className="service-mark-as-complete__ratings"
              // style={{
              //   gridTemplateColumns: `repeat(${ratings.length}, 1fr)`,
              // }}
            >
              {ratings.map(([key, rating]) => {
                const isSelected = formValues.rating === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`service-mark-as-complete__rating-option ${
                      isSelected
                        ? 'service-mark-as-complete__rating-option--selected'
                        : ''
                    }`}
                    onClick={() => onSelectRating(key)}
                    style={{
                      ['--rating-color' as string]: rating.color,
                      ['--rating-bg-color' as string]: createColorShades(
                        THEME.LIGHT as ThemeType,
                        rating.color,
                        rating.color,
                      )[`${rating.color}_FILL`],
                    }}>
                    <span className="service-mark-as-complete__rating-option__logo">
                      {rating.logo}
                    </span>
                    <Typography.Text className="service-mark-as-complete__rating-option__label">
                      {rating.name}
                    </Typography.Text>
                  </button>
                );
              })}
            </div>

            {selectedRatingCategory ? (
              <div className="service-mark-as-complete__feedback-section">
                <label className="service-mark-as-complete__feedback-label">
                  What made this experience {selectedRatingCategory.name}?
                </label>
                <button
                  type="button"
                  className="service-mark-as-complete__description-trigger"
                  onClick={openDescriptionView}>
                  {formValues.feedback.trim() ? (
                    <span className="service-mark-as-complete__description-trigger__text">
                      {formValues.feedback}
                    </span>
                  ) : (
                    <span className="service-mark-as-complete__description-trigger__placeholder">
                      {selectedRatingCategory.placeholder ??
                        'What did they do that really made a difference?'}
                    </span>
                  )}
                </button>

                <Typography.Text className="service-mark-as-complete__upload-label">
                  Want to support your feedback with an image or video?
                </Typography.Text>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.mp4,image/jpeg,image/png,video/mp4"
                  multiple
                  className="service-mark-as-complete__file-input"
                  onChange={handleAddAttachment}
                  aria-hidden
                />
                {attachments.length <
                  TQ_VALIDATION.MAX_SERVICE_REQUEST_RESPONSE_FEEDBACK_ATTACHMENTS_COUNT && (
                  <Button
                    htmlType="button"
                    onClick={() => fileInputRef.current?.click()}
                    icon={<UploadOutlined />}
                    className="service-mark-as-complete__upload-btn"
                    block>
                    Click to upload
                  </Button>
                )}
                <div className="service-mark-as-complete__attachment-list">
                  {attachmentPreviews.map(({ file, url }) => (
                    <div
                      key={url}
                      className="service-mark-as-complete__attachment-preview">
                      {file.type.startsWith('video/') ? (
                        <video
                          src={url}
                          className="service-mark-as-complete__attachment-preview__img"
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          src={url}
                          alt=""
                          className="service-mark-as-complete__attachment-preview__img"
                        />
                      )}
                      <Button
                        danger
                        size="small"
                        htmlType="button"
                        icon={
                          <TMIcon
                            name="trash-outline"
                            size={16}
                            color="currentColor"
                          />
                        }
                        className="service-mark-as-complete__attachment-preview__remove"
                        onClick={() => handleRemoveAttachment(file)}>
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className="service-mark-as-complete__footer">
        <Button
          type="primary"
          className="siteBtn accentBtnV1 service-mark-as-complete__submit"
          onClick={submitFeedback}
          loading={isSaving}
          disabled={isSubmitDisabled}
          block>
          Complete & Submit Feedback
        </Button>
      </div>
    </div>
  );
};

export default memo(ServiceMarkAsComplete);
